// On-device diagnostics for GPU 2D-canvas context loss - the "white screen"
// that the recovery in GameCanvas heals (a canvas whose backing store the OS
// evicts under memory pressure, seen on some Android phones). We can't reproduce
// the eviction in a lab, so we record when it happens in the wild to learn how
// often it fires and on which screen.
//
// Privacy: this sends NOTHING off-device. It keeps a tiny running summary in
// localStorage (read it with getCanvasContextEventLog()) and mirrors each event
// to the console. It also calls the SAME dormant analytics hook donate.ts uses
// (window.plausible?.(...)), which is a no-op until analytics is actually wired
// up (#35) - so it adds no tracking today, and reporting it off-device would
// need the privacy policy updated first.

const STORE_KEY = "ar_canvas_ctx_events";

export interface CanvasContextEventLog {
  lost: number; // evictions (deduped per event, not per stacked layer)
  restored: number; // recoveries afterwards
  firstAt: string | null;
  lastAt: string | null;
  lastScreen: string | null; // route path where the last event happened
}

const EMPTY: CanvasContextEventLog = {
  lost: 0,
  restored: 0,
  firstAt: null,
  lastAt: null,
  lastScreen: null,
};

function read(): CanvasContextEventLog {
  try {
    const raw = window.localStorage.getItem(STORE_KEY);
    return raw ? { ...EMPTY, ...JSON.parse(raw) } : { ...EMPTY };
  } catch {
    return { ...EMPTY };
  }
}

function write(log: CanvasContextEventLog): void {
  try {
    window.localStorage.setItem(STORE_KEY, JSON.stringify(log));
  } catch {
    /* private mode / storage full - diagnostics are best-effort */
  }
}

/** Read the running on-device summary (e.g. from the console when debugging). */
export function getCanvasContextEventLog(): CanvasContextEventLog {
  return read();
}

function currentScreen(): string {
  try {
    return window.location?.pathname ?? "";
  } catch {
    return "";
  }
}

// Fire the dormant analytics hook (see donate.ts). No-op unless a Plausible
// script is present; never let it throw into the caller.
function ping(event: string, props: Record<string, unknown>): void {
  try {
    (window as any).plausible?.(event, { props });
  } catch {
    /* tracking must never break rendering */
  }
}

export function recordContextLost(): void {
  const log = read();
  log.lost += 1;
  const now = new Date().toISOString();
  log.firstAt = log.firstAt ?? now;
  log.lastAt = now;
  log.lastScreen = currentScreen();
  write(log);
  console.warn(`[canvas] GPU 2D context lost (#${log.lost}) on ${log.lastScreen || "?"}`);
  ping("canvas_context_lost", {
    screen: log.lastScreen,
    // Coarse RAM bucket (0.25..8), handy to correlate with memory pressure and
    // not identifying. Absent on browsers that don't expose it.
    deviceMemory: (navigator as any).deviceMemory ?? null,
  });
}

export function recordContextRestored(): void {
  const log = read();
  log.restored += 1;
  log.lastAt = new Date().toISOString();
  write(log);
  console.info(`[canvas] GPU 2D context restored (#${log.restored})`);
  ping("canvas_context_restored", {});
}
